import type { SelectOption } from '@enamelai/design-system'
import { getEnv } from '@utils/env'
import type { SchedulerSettingsType } from './Scheduler.component'

/** Figma drops "View mode"; the Appointment calendar still reads `view_mode`. */
export const SCHEDULER_VIEW_MODE_ENABLED =
  getEnv('REACT_APP_SCHEDULER_VIEW_MODE_ENABLED', 'false') === 'true'

const formatHourOption = (hour: number): string => {
  const period = hour % 24 < 12 ? 'AM' : 'PM'
  const display = hour % 12 === 0 ? 12 : hour % 12
  return `${String(display).padStart(2, '0')}:00 ${period}`
}

const hourOption = (hour: number): SelectOption => ({
  value: String(hour),
  label: formatHourOption(hour),
})

export const START_HOUR_OPTIONS: SelectOption[] = Array.from(
  { length: 24 },
  (_, hour) => hourOption(hour),
)

export const getEndHourOptions = (startHour: number): SelectOption[] =>
  Array.from({ length: 24 - startHour }, (_, index) =>
    hourOption(startHour + index + 1),
  )

const minutesOptions = (
  values: number[],
  labelFor: (value: number) => string = (value) => `${value} minutes`,
): SelectOption[] =>
  values.map((value) => ({ value: String(value), label: labelFor(value) }))

export const TIME_SLOT_INTERVAL_OPTIONS = minutesOptions([15, 30, 60])

export const BUFFER_OPTIONS = minutesOptions([0, 5, 10, 15, 20, 25, 30], (v) =>
  v === 0 ? 'None' : `${v} minutes`,
)

export const CALENDAR_INTERVAL_OPTIONS = minutesOptions([5, 10, 15, 30], (v) =>
  v === 15 ? '15 minutes (default)' : `${v} minutes`,
)

export const YES_NO_OPTIONS: SelectOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

export const DEFAULT_CALENDAR_VIEW_OPTIONS: SelectOption[] = [
  { value: 'Day', label: 'Day' },
  { value: 'Week', label: 'Week' },
  { value: 'Month', label: 'Month' },
]

export const VIEW_MODE_OPTIONS: SelectOption[] = [
  { value: 'Fixed', label: 'Fixed' },
  { value: 'Full', label: 'Full' },
]

const formatHours = (hours: string): string =>
  `${hours} ${hours === '1' ? 'hour' : 'hours'}`

/** Reminder and auto-confirm values are stored as hour counts. */
const HOUR_COUNT_OPTIONS: SelectOption[] = [
  '1',
  '2',
  '4',
  '12',
  '24',
  '48',
].map((hours) => ({ value: hours, label: formatHours(hours) }))

export const getHourCountOptions = (current: string): SelectOption[] =>
  current && !HOUR_COUNT_OPTIONS.some((option) => option.value === current)
    ? [...HOUR_COUNT_OPTIONS, { value: current, label: formatHours(current) }]
    : HOUR_COUNT_OPTIONS

export const RESCHEDULE_WINDOW_OPTIONS: SelectOption[] = [
  {
    value: '12 hours before appointment',
    label: '12 hours before appointment',
  },
  { value: '24 hours before appointment', label: 'A day before appointment' },
  { value: '48 hours before appointment', label: '2 days before appointment' },
]

export const NO_SHOW_ACTION_OPTIONS: SelectOption[] = [
  'Flag on patient record',
  'Charge no-show fee',
  'Restrict future booking',
].map((value) => ({ value, label: value }))

export const LATE_CANCELLATION_OPTIONS: SelectOption[] = [
  'Less than 4 hours',
  'Less than 12 hours',
  'Less than 24 hours',
].map((value) => ({ value, label: value }))

type ToggleKey = {
  [K in keyof SchedulerSettingsType]: SchedulerSettingsType[K] extends boolean
    ? K
    : never
}[keyof SchedulerSettingsType]

/** Row-major order, matching the 3-column Figma grid. */
export const SCHEDULER_TOGGLES: { key: ToggleKey; label: string }[] = [
  { key: 'show_appointment_type', label: 'Show appt. type information' },
  { key: 'show_appointment_duration', label: 'Show appt. duration' },
  { key: 'overlap_appointments', label: 'Overlap appointments' },
  { key: 'status_indicator', label: 'Status indicator' },
  { key: 'allow_double_booking', label: 'Allow double booking' },
  {
    key: 'schedule_recall_automatically',
    label: 'Schedule recall automatically',
  },
  { key: 'show_cancelled_appointments', label: 'Show cancelled appt.' },
  { key: 'show_provider_information', label: 'Show provider information' },
  {
    key: 'require_patient_confirmation',
    label: 'Require patient confirmation',
  },
]
